import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '@shared/components/ui/Toaster';

const Signup: React.FC = () => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { signUp, user } = useAuth();
  
  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      const destination = '/dashboard';
      navigate(destination);
    }
  }, [user, navigate]);

  const passwordStrength = (): { strength: string; color: string } => {
    if (password.length === 0) return { strength: 'Empty', color: 'bg-gray-300' };
    if (password.length < 6) return { strength: 'Weak', color: 'bg-red-500' };
    
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    
    if (score === 4) return { strength: 'Strong', color: 'bg-green-500' };
    if (score >= 2) return { strength: 'Medium', color: 'bg-yellow-500' };
    return { strength: 'Weak', color: 'bg-red-500' };
  };

  const { strength, color } = passwordStrength();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!name.trim()) {
      setError('Please enter your full name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Signup - Creating account for:', email, 'with name:', name);
      await signUp(email, password, name);

      // Success! Redirect to subscription page to complete setup payment
      toast.success('Account created successfully! Please complete your setup payment to get started.');
      setTimeout(() => {
        navigate('/subscription');
      }, 1500);

    } catch (error: any) {
      console.error('Signup error:', error);

      // Parse error message for user-friendly display
      let errorMsg = error?.message || 'Failed to create an account';

      // Handle specific error scenarios
      if (errorMsg.includes('An account with this email already exists')) {
        setError('This email is already registered. Please sign in instead.');
        toast.info('Redirecting to sign in...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);

      } else if (errorMsg.includes('weak_password') || errorMsg.includes('Password is too weak')) {
        setError('Password is too weak. Please use a stronger password with uppercase, numbers, and special characters.');

      } else if (errorMsg.includes('invalid_email')) {
        setError('Please enter a valid email address.');

      } else if (errorMsg.includes('Password should be at least')) {
        setError(errorMsg);

      } else {
        // Generic error
        setError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-12">
      <div className="container max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Start Your Career Transformation</h1>
          <p className="text-muted-foreground mt-2">
            Create an account to discover your hidden potential
          </p>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-8">
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 mb-6">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-muted-foreground">Password strength:</span>
                  <span className="text-xs font-medium">{strength}</span>
                </div>
                <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full ${color}`} style={{ width: `${(strength === 'Empty' ? 0 : strength === 'Weak' ? 33 : strength === 'Medium' ? 66 : 100)}%` }}></div>
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                <li className="flex items-center text-xs text-muted-foreground">
                  <CheckCircle className={`h-3 w-3 mr-1 ${password.length >= 8 ? 'text-green-500' : 'text-gray-300'}`} />
                  At least 8 characters
                </li>
                <li className="flex items-center text-xs text-muted-foreground">
                  <CheckCircle className={`h-3 w-3 mr-1 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-gray-300'}`} />
                  At least one uppercase letter
                </li>
                <li className="flex items-center text-xs text-muted-foreground">
                  <CheckCircle className={`h-3 w-3 mr-1 ${/[0-9]/.test(password) ? 'text-green-500' : 'text-gray-300'}`} />
                  At least one number
                </li>
                <li className="flex items-center text-xs text-muted-foreground">
                  <CheckCircle className={`h-3 w-3 mr-1 ${/[^A-Za-z0-9]/.test(password) ? 'text-green-500' : 'text-gray-300'}`} />
                  At least one special character
                </li>
              </ul>
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                <>
                  Start Your Career Journey
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <p className="text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-border/40">
          <div className="flex items-start gap-4">
            <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold">Why Join FutureLens?</h3>
              <p className="text-sm text-muted-foreground mt-1">
                By creating an account, you'll get access to our proprietary BRAG + DREAM frameworks, 
                powered by triple-AI analysis (GPT-4.1, Claude 3.5, Gemini Ultra) to transform your 
                career with actionable insights and opportunities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;